import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, Package, Search, Plus, Pencil, Trash2, Image as ImageIcon,
  X, TrendingUp, DollarSign, ShoppingCart, AlertTriangle, BarChart3,
  History, MoreHorizontal, Eye, RefreshCw, Clock, Download, Upload, FileSpreadsheet,
  ArrowUpDown, ArrowUp, ArrowDown, CheckSquare, Square, MinusSquare,
  Power, PowerOff, Globe, Ban, ChevronDown, Layers
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import LanguageSwitcher from '../components/LanguageSwitcher';

function AdminProductsPro({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  
  const [salesReport, setSalesReport] = useState(null);
  const [stockAlerts, setStockAlerts] = useState(null);
  
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    active: 'all'
  });
  
  // Sorting state
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Selection state
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  
  // Variant grouping
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [groupParentId, setGroupParentId] = useState('');
  const [groupVariantLabels, setGroupVariantLabels] = useState({});
  
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [showPriceChange, setShowPriceChange] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetails, setProductDetails] = useState(null);
  const [groupPricing, setGroupPricing] = useState([]);
  
  // Import/Export state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showGalleryPicker, setShowGalleryPicker] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  
  const [productForm, setProductForm] = useState({
    name: '',
    name_en: '',
    name_kz: '',
    description: '',
    description_en: '',
    description_kz: '',
    retail_price: '',
    site_price: '',
    category: '',
    image_url: '',
    hover_image_url: '',
    sku: '',
    article: '',      // Артикул для 1С
    code_1c: '',      // Код номенклатуры 1С
    ntin: '',         // NTIN (НКТ) код
    unit_of_measure: '',
    minimum_order_qty: 1,
    order_step: 1,
    is_active: true,
    online_available: true,
    daily_limit: 0,
  });

  const [priceChangeForm, setPriceChangeForm] = useState({
    new_price: '',
    reason: ''
  });

  const [newGroupPrice, setNewGroupPrice] = useState({ group_id: '', price_type: 'fixed', price: '', discount_percentage: '' });

  const getLocalizedName = (item, field = 'name') => {
    if (!item) return '';
    return item[`${field}_${currentLang}`] || item[`${field}_ru`] || item[field] || '';
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'sales') {
      fetchSalesReport();
    } else if (activeTab === 'stock') {
      fetchStockAlerts();
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/admin/products`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/client-groups`, { headers: getAuthHeaders() }),
        axios.get(`${API}/settings/categories`, { headers: getAuthHeaders() }),
        axios.get(`${API}/settings/units`, { headers: getAuthHeaders() }),
      ]);
      if (results[0].status === 'fulfilled') setProducts(results[0].value.data);
      if (results[1].status === 'fulfilled') setGroups(results[1].value.data);
      if (results[2].status === 'fulfilled') setCategories(results[2].value.data);
      if (results[3].status === 'fulfilled') setUnits(results[3].value.data);
    } catch (error) {
      toast.error('Не удалось загрузить товары');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesReport = async () => {
    try {
      const response = await axios.get(`${API}/admin/products/sales-report?period=month`, {
        headers: getAuthHeaders()
      });
      setSalesReport(response.data);
    } catch (error) {
      console.error('Failed to fetch sales report');
    }
  };

  const fetchStockAlerts = async () => {
    try {
      const response = await axios.get(`${API}/admin/products/stock-alerts`, {
        headers: getAuthHeaders()
      });
      setStockAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch stock alerts');
    }
  };

  const fetchProductDetails = async (productId) => {
    try {
      const response = await axios.get(`${API}/admin/products/${productId}/details`, {
        headers: getAuthHeaders()
      });
      setProductDetails(response.data);
    } catch (error) {
      toast.error('Не удалось загрузить данные товара');
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? getLocalizedName(cat, 'name') : categoryId;
  };

  const getUnitName = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    return unit ? getLocalizedName(unit, 'short_name') : unitId;
  };

  const filteredProducts = useMemo(() => {
    let result = products.filter(product => {
      if (filters.category !== 'all' && product.category !== filters.category) return false;
      if (filters.active !== 'all') {
        if (filters.active === 'active' && !product.is_active) return false;
        if (filters.active === 'inactive' && product.is_active) return false;
      }
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchName = product.name?.toLowerCase().includes(searchLower);
        const matchNameRu = product.name_ru?.toLowerCase().includes(searchLower);
        const matchNameEn = product.name_en?.toLowerCase().includes(searchLower);
        const matchArticle = product.article?.toString().toLowerCase().includes(searchLower);
        const matchSku = product.sku?.toLowerCase().includes(searchLower);
        if (!matchName && !matchNameRu && !matchNameEn && !matchArticle && !matchSku) return false;
      }
      return true;
    });
    
    // Sort products
    result.sort((a, b) => {
      let compareA, compareB;
      
      switch (sortField) {
        case 'name':
          compareA = a.name.toLowerCase();
          compareB = b.name.toLowerCase();
          break;
        case 'price':
          compareA = a.retail_price || 0;
          compareB = b.retail_price || 0;
          break;
        case 'date':
          compareA = a.created_at || '';
          compareB = b.created_at || '';
          break;
        case 'status':
          compareA = a.is_active ? 1 : 0;
          compareB = b.is_active ? 1 : 0;
          break;
        default:
          compareA = a.name.toLowerCase();
          compareB = b.name.toLowerCase();
      }
      
      if (compareA < compareB) return sortOrder === 'asc' ? -1 : 1;
      if (compareA > compareB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [products, filters, sortField, sortOrder]);
  
  // Selection helpers
  const isAllSelected = filteredProducts.length > 0 && filteredProducts.every(p => selectedProducts.has(p.id));
  const isSomeSelected = filteredProducts.some(p => selectedProducts.has(p.id));
  
  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map(p => p.id)));
    }
  };
  
  const handleSelectProduct = (productId) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };
  
  const handleBulkDelete = async () => {
    if (selectedProducts.size === 0) return;
    
    setBulkDeleting(true);
    try {
      const response = await axios.post(
        `${API}/admin/products/bulk-delete`,
        { product_ids: Array.from(selectedProducts) },
        { headers: getAuthHeaders() }
      );
      toast.success(`Удалено ${response.data.deleted_count} товаров`);
      setSelectedProducts(new Set());
      setShowBulkDeleteDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Не удалось удалить товары');
    } finally {
      setBulkDeleting(false);
    }
  };
  
  const handleBulkUpdate = async (action) => {
    if (selectedProducts.size === 0) return;
    
    setBulkUpdating(true);
    try {
      const response = await axios.post(
        `${API}/admin/products/bulk-update`,
        { 
          product_ids: Array.from(selectedProducts),
          action: action
        },
        { headers: getAuthHeaders() }
      );
      toast.success(response.data.message);
      setSelectedProducts(new Set());
      fetchData();
    } catch (error) {
      toast.error('Не удалось обновить товары');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Open grouping dialog
  const openGroupDialog = () => {
    if (selectedProducts.size < 2) {
      toast.error('Выберите минимум 2 товара для группировки');
      return;
    }
    const ids = Array.from(selectedProducts);
    setGroupParentId(ids[0]);
    const labels = {};
    ids.forEach(id => {
      const p = products.find(pr => pr.id === id);
      // Try to extract weight from name (e.g. "Круассан 90гр" -> "90г")
      const match = (p?.name_ru || p?.name || '').match(/(\d+)\s*[гgГG][рrРR]*/);
      labels[id] = match ? `${match[1]}г` : '';
    });
    setGroupVariantLabels(labels);
    setShowGroupDialog(true);
  };

  const handleGroupVariants = async () => {
    const ids = Array.from(selectedProducts);
    const variants = ids
      .filter(id => id !== groupParentId)
      .map((id, i) => ({ id, label: groupVariantLabels[id] || '', sort: i + 1 }));

    try {
      await axios.post(
        `${API}/admin/products/variants/group`,
        {
          parent_id: groupParentId,
          parent_label: groupVariantLabels[groupParentId] || '',
          variants
        },
        { headers: getAuthHeaders() }
      );
      toast.success('Товары сгруппированы');
      setShowGroupDialog(false);
      setSelectedProducts(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка группировки');
    }
  };

  const handleUngroupProducts = async () => {
    if (selectedProducts.size === 0) return;
    try {
      await axios.post(
        `${API}/admin/products/variants/ungroup`,
        { product_ids: Array.from(selectedProducts) },
        { headers: getAuthHeaders() }
      );
      toast.success('Группировка снята');
      setSelectedProducts(new Set());
      fetchData();
    } catch (error) {
      toast.error('Ошибка разгруппировки');
    }
  };
  
  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    return sortOrder === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-emerald-600" /> 
      : <ArrowDown className="w-4 h-4 text-emerald-600" />;
  };

  const handleViewProduct = async (product) => {
    setSelectedProduct(product);
    await fetchProductDetails(product.id);
    setShowProductDetails(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...productForm,
        name_ru: productForm.name,
        description_ru: productForm.description,
        retail_price: parseFloat(productForm.retail_price) || 0,
        site_price: parseFloat(productForm.site_price) || parseFloat(productForm.retail_price) || 0,
        minimum_order_qty: parseFloat(productForm.minimum_order_qty),
        order_step: parseFloat(productForm.order_step),
        daily_limit: parseInt(productForm.daily_limit) || 0,
      };

      let savedProductId = editingProduct?.id;

      if (editingProduct) {
        await axios.put(
          `${API}/admin/products/${editingProduct.id}`,
          data,
          { headers: getAuthHeaders() }
        );
        toast.success('Товар обновлён');
      } else {
        const res = await axios.post(`${API}/products`, data, { headers: getAuthHeaders() });
        savedProductId = res.data?.id;
        toast.success('Товар создан');
      }
      
      // Save pending group prices for new product
      if (savedProductId && groupPricing.length > 0 && !editingProduct) {
        for (const gp of groupPricing) {
          try {
            const params = new URLSearchParams({ group_id: gp.group_id, price_type: gp.price_type || 'fixed' });
            if (gp.special_price) params.append('special_price', gp.special_price);
            if (gp.discount_percentage) params.append('discount_percentage', gp.discount_percentage);
            await axios.post(`${API}/admin/products/${savedProductId}/group-pricing?${params}`, {}, { headers: getAuthHeaders() });
          } catch (err) { console.error('Failed to save group price:', err); }
        }
      }
      
      setShowProductDialog(false);
      setEditingProduct(null);
      resetProductForm();
      fetchData();
    } catch (error) {
      toast.error('Не удалось сохранить товар');
    }
  };

  const handlePriceChange = async () => {
    if (!priceChangeForm.new_price) {
      toast.error('Укажите новую цену');
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/products/${selectedProduct.id}/price-change?new_price=${priceChangeForm.new_price}${priceChangeForm.reason ? `&reason=${encodeURIComponent(priceChangeForm.reason)}` : ''}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Цена обновлена');
      setShowPriceChange(false);
      setPriceChangeForm({ new_price: '', reason: '' });
      fetchData();
      if (productDetails) {
        fetchProductDetails(selectedProduct.id);
      }
    } catch (error) {
      toast.error('Не удалось изменить цену');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Удалить товар?')) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, { headers: getAuthHeaders() });
      toast.success('Товар удалён');
      fetchData();
    } catch (error) {
      toast.error('Не удалось удалить товар');
    }
  };

  const openEditProduct = async (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      name_en: product.name_en || '',
      name_kz: product.name_kz || '',
      description: product.description || '',
      description_en: product.description_en || '',
      description_kz: product.description_kz || '',
      retail_price: (product.retail_price || 0).toString(),
      site_price: (product.site_price || product.retail_price || 0).toString(),
      category: product.category,
      image_url: product.image_url || '',
      hover_image_url: product.hover_image_url || '',
      sku: product.sku || '',
      article: product.article || '',
      code_1c: product.code_1c || '',
      ntin: product.ntin || '',
      unit_of_measure: product.unit_of_measure || '',
      minimum_order_qty: product.minimum_order_qty || 1,
      order_step: product.order_step || 1,
      is_active: product.is_active !== false,
      online_available: product.online_available !== false,
      daily_limit: product.daily_limit || 0,
    });
    
    try {
      const res = await axios.get(
        `${API}/admin/products/${product.id}/group-pricing`,
        { headers: getAuthHeaders() }
      );
      setGroupPricing(res.data);
    } catch (error) {
      setGroupPricing([]);
    }
    
    setShowProductDialog(true);
  };

  const openCreateProduct = () => {
    setEditingProduct(null);
    resetProductForm();
    setGroupPricing([]);
    setShowProductDialog(true);
  };

  const handleAddGroupPrice = async () => {
    if (!newGroupPrice.group_id) {
      toast.error('Выберите группу');
      return;
    }
    
    if (newGroupPrice.price_type === 'fixed' && !newGroupPrice.price) {
      toast.error('Укажите фиксированную цену');
      return;
    }
    
    if (newGroupPrice.price_type === 'percentage' && !newGroupPrice.discount_percentage) {
      toast.error('Укажите процент скидки');
      return;
    }
    
    if (!editingProduct) {
      // New product - save to local state, will be sent after product creation
      const group = availableGroups.find(g => g.id === newGroupPrice.group_id);
      setGroupPricing(prev => [...prev, {
        group_id: newGroupPrice.group_id,
        group_name: group?.name || '',
        price_type: newGroupPrice.price_type,
        special_price: newGroupPrice.price_type === 'fixed' ? parseFloat(newGroupPrice.price) : null,
        discount_percentage: newGroupPrice.price_type === 'percentage' ? parseFloat(newGroupPrice.discount_percentage) : null,
      }]);
      setNewGroupPrice({ group_id: '', price_type: 'fixed', price: '', discount_percentage: '' });
      toast.success('Цена для группы добавлена (сохранится при создании товара)');
      return;
    }
    
    try {
      const params = new URLSearchParams({
        group_id: newGroupPrice.group_id,
        price_type: newGroupPrice.price_type
      });
      
      if (newGroupPrice.price_type === 'fixed') {
        params.append('special_price', newGroupPrice.price);
      } else {
        params.append('discount_percentage', newGroupPrice.discount_percentage);
      }
      
      await axios.post(
        `${API}/admin/products/${editingProduct.id}/group-pricing?${params}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Цена для группы добавлена');
      
      const res = await axios.get(
        `${API}/admin/products/${editingProduct.id}/group-pricing`,
        { headers: getAuthHeaders() }
      );
      setGroupPricing(res.data);
      setNewGroupPrice({ group_id: '', price_type: 'fixed', price: '', discount_percentage: '' });
    } catch (error) {
      toast.error('Не удалось добавить цену');
    }
  };

  const handleRemoveGroupPrice = async (groupId) => {
    try {
      await axios.delete(
        `${API}/admin/products/${editingProduct.id}/group-pricing/${groupId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Цена удалена');
      
      const res = await axios.get(
        `${API}/admin/products/${editingProduct.id}/group-pricing`,
        { headers: getAuthHeaders() }
      );
      setGroupPricing(res.data);
    } catch (error) {
      toast.error('Не удалось удалить цену');
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      name_en: '',
      name_kz: '',
      description: '',
      description_en: '',
      description_kz: '',
      retail_price: '',
      site_price: '',
      category: categories[0]?.id || '',
      image_url: '',
      hover_image_url: '',
      sku: '',
      article: '',
      code_1c: '',
      ntin: '',
      unit_of_measure: units[0]?.id || '',
      minimum_order_qty: 1,
      order_step: 1,
      is_active: true,
      online_available: true,
      daily_limit: 0,
    });
    setGroupPricing([]);
    setNewGroupPrice({ group_id: '', price_type: 'fixed', price: '', discount_percentage: '' });
  };

  // Import/Export functions
  const handleExportProducts = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/products-io/export`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `products_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Экспорт завершен');
    } catch (error) {
      toast.error('Ошибка экспорта');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/products-io/template`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'products_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Шаблон скачан');
    } catch (error) {
      toast.error('Ошибка загрузки шаблона');
    }
  };

  const handleImportProducts = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/products-io/import`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data);
      
      if (response.data.created > 0 || response.data.updated > 0) {
        toast.success(`Импорт завершен: ${response.data.created} создано, ${response.data.updated} обновлено`);
        fetchData(); // Refresh products list
      }
      
      if (response.data.errors?.length > 0) {
        toast.warning(`Есть ошибки: ${response.data.total_errors}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка импорта');
      setImportResult({ success: false, errors: [error.response?.data?.detail || 'Неизвестная ошибка'] });
    } finally {
      setImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU').format(amount || 0) + ' ₸';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const availableGroups = groups.filter(g => 
    !groupPricing.some(gp => gp.group_id === g.id)
  );

  // Handle image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Неподдерживаемый формат. Разрешены: JPG, PNG, GIF, WebP');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Файл слишком большой. Максимум 5MB');
      return;
    }
    
    setUploadingImage(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        setProductForm(prev => ({ ...prev, image_url: response.data.url }));
        toast.success('Изображение загружено');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка загрузки изображения');
    } finally {
      setUploadingImage(false);
      // Reset input
      event.target.value = '';
    }
  };

  // Open gallery picker
  const openGalleryPicker = async () => {
    setShowGalleryPicker(true);
    setGalleryLoading(true);
    try {
      const response = await axios.get(`${API}/media/gallery?limit=100`, { headers: getAuthHeaders() });
      setGalleryImages(response.data.items || []);
    } catch (error) {
      toast.error('Ошибка загрузки галереи');
    } finally {
      setGalleryLoading(false);
    }
  };

  // Select image from gallery
  const selectGalleryImage = (url) => {
    setProductForm(prev => ({ ...prev, image_url: url }));
    setShowGalleryPicker(false);
    toast.success('Изображение выбрано');
  };

  // Upload hover image
  const handleHoverImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Неподдерживаемый формат. Разрешены: JPG, PNG, GIF, WebP');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Файл слишком большой. Максимум 5MB');
      return;
    }
    
    setUploadingImage(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        setProductForm(prev => ({ ...prev, hover_image_url: response.data.url }));
        toast.success('Hover-изображение загружено');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка загрузки');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  // Select hover image from gallery
  const selectHoverGalleryImage = (url) => {
    setProductForm(prev => ({ ...prev, hover_image_url: url }));
    setShowGalleryPicker(false);
    toast.success('Hover-изображение выбрано');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <div className="text-lg text-gray-600">Загрузка товаров...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}/admin/settings`}>
                <Button variant="ghost" size="sm" data-testid="back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Управление
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Управление товарами</h1>
                <p className="text-sm text-gray-500">Каталог, цены, остатки и статистика</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              
              {/* Import/Export Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="import-export-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Excel
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={handleDownloadTemplate}>
                    <Download className="w-4 h-4 mr-2" />
                    Скачать шаблон
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportProducts} disabled={exporting}>
                    <Download className="w-4 h-4 mr-2" />
                    {exporting ? 'Экспорт...' : 'Экспорт товаров'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowImportDialog(true)}>
                    <Upload className="w-4 h-4 mr-2" />
                    Импорт товаров
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button onClick={openCreateProduct} className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-product-btn">
                <Plus className="w-4 h-4 mr-2" />
                Добавить товар
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="list" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Каталог ({products.length})
            </TabsTrigger>
            <TabsTrigger value="sales" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Статистика продаж
            </TabsTrigger>
            <TabsTrigger value="stock" className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Остатки
            </TabsTrigger>
          </TabsList>

          {/* Products List Tab */}
          <TabsContent value="list">
            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-sm text-gray-600">Поиск</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Название или артикул..."
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="pl-10"
                        data-testid="search-input"
                      />
                    </div>
                  </div>

                  <div className="w-[180px]">
                    <Label className="text-sm text-gray-600">Категория</Label>
                    <Select value={filters.category} onValueChange={(v) => setFilters(prev => ({ ...prev, category: v }))}>
                      <SelectTrigger data-testid="category-filter-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Все категории</SelectItem>
                        {categories.map(c => (
                          <SelectItem key={c.id} value={c.id}>{getLocalizedName(c, 'name')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-[140px]">
                    <Label className="text-sm text-gray-600">Статус</Label>
                    <Select value={filters.active} onValueChange={(v) => setFilters(prev => ({ ...prev, active: v }))}>
                      <SelectTrigger data-testid="status-filter-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Все</SelectItem>
                        <SelectItem value="active">Активные</SelectItem>
                        <SelectItem value="inactive">Неактивные</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="w-[160px]">
                    <Label className="text-sm text-gray-600">Сортировка</Label>
                    <Select value={sortField} onValueChange={(v) => { setSortField(v); setSortOrder('asc'); }}>
                      <SelectTrigger data-testid="sort-field-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="name">По названию</SelectItem>
                        <SelectItem value="price">По цене</SelectItem>
                        <SelectItem value="date">По дате</SelectItem>
                        <SelectItem value="status">По статусу</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                    className="h-10"
                    data-testid="sort-order-toggle"
                  >
                    {getSortIcon(sortField)}
                    <span className="ml-1">{sortOrder === 'asc' ? 'А-Я' : 'Я-А'}</span>
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFilters({ search: '', category: 'all', active: 'all' });
                      setSortField('name');
                      setSortOrder('asc');
                      setSelectedProducts(new Set());
                    }}
                    data-testid="reset-filters-btn"
                  >
                    Сбросить
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Bulk actions toolbar */}
            {selectedProducts.size > 0 && (
              <Card className="mb-4 border-emerald-200 bg-emerald-50">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <CheckSquare className="w-5 h-5 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-800">
                        Выбрано: {selectedProducts.size} {selectedProducts.size === 1 ? 'товар' : selectedProducts.size < 5 ? 'товара' : 'товаров'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Status actions dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={bulkUpdating}
                            data-testid="bulk-status-dropdown"
                          >
                            <Power className="w-4 h-4 mr-2" />
                            Статус
                            <ChevronDown className="w-4 h-4 ml-1" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            onClick={() => handleBulkUpdate('activate')}
                            data-testid="bulk-activate-btn"
                          >
                            <Power className="w-4 h-4 mr-2 text-green-600" />
                            Активировать (показать на сайте)
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleBulkUpdate('deactivate')}
                            data-testid="bulk-deactivate-btn"
                          >
                            <PowerOff className="w-4 h-4 mr-2 text-gray-500" />
                            Деактивировать (скрыть с сайта)
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      
                      {/* Online sales actions dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={bulkUpdating}
                            data-testid="bulk-online-dropdown"
                          >
                            <Globe className="w-4 h-4 mr-2" />
                            Онлайн
                            <ChevronDown className="w-4 h-4 ml-1" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            onClick={() => handleBulkUpdate('online_enable')}
                            data-testid="bulk-online-enable-btn"
                          >
                            <Globe className="w-4 h-4 mr-2 text-blue-600" />
                            Включить онлайн-продажу
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleBulkUpdate('online_disable')}
                            data-testid="bulk-online-disable-btn"
                          >
                            <Ban className="w-4 h-4 mr-2 text-gray-500" />
                            Отключить онлайн-продажу
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      
                      {/* Variant grouping */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={openGroupDialog}
                        className="border-blue-200 text-blue-700 hover:bg-blue-50"
                        data-testid="group-variants-btn"
                      >
                        <Layers className="w-4 h-4 mr-2" />
                        Группировать
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleUngroupProducts}
                        className="border-orange-200 text-orange-700 hover:bg-orange-50"
                        data-testid="ungroup-variants-btn"
                      >
                        Разгруппировать
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedProducts(new Set())}
                        data-testid="clear-selection-btn"
                      >
                        Снять выбор
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setShowBulkDeleteDialog(true)}
                        data-testid="bulk-delete-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Удалить
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Products Grid */}
            {filteredProducts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-lg font-medium">Товары не найдены</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Select all header */}
                <div className="mb-3 flex items-center gap-3">
                  <button
                    onClick={handleSelectAll}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    data-testid="select-all-btn"
                  >
                    {isAllSelected ? (
                      <CheckSquare className="w-5 h-5 text-emerald-600" />
                    ) : isSomeSelected ? (
                      <MinusSquare className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <Square className="w-5 h-5" />
                    )}
                    <span>{isAllSelected ? 'Снять все' : 'Выбрать все'}</span>
                  </button>
                  <span className="text-sm text-gray-400">
                    ({filteredProducts.length} {filteredProducts.length === 1 ? 'товар' : filteredProducts.length < 5 ? 'товара' : 'товаров'})
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredProducts.map((product) => (
                    <Card 
                      key={product.id} 
                      className={`relative transition-all ${!product.is_active ? 'opacity-60' : ''} ${selectedProducts.has(product.id) ? 'ring-2 ring-emerald-500 bg-emerald-50/50' : ''}`}
                      data-testid={`product-card-${product.id}`}
                    >
                      <CardContent className="p-4">
                        {/* Selection checkbox */}
                        <div className="absolute top-2 left-2 z-10">
                          <Checkbox
                            checked={selectedProducts.has(product.id)}
                            onCheckedChange={() => handleSelectProduct(product.id)}
                            className="bg-white border-2"
                            data-testid={`product-checkbox-${product.id}`}
                          />
                        </div>
                        
                        <div className="aspect-square bg-gray-100 rounded-lg mb-3 flex items-center justify-center overflow-hidden relative">
                          {product.image_url ? (
                            <img
                              src={product.image_url}
                              alt={getLocalizedName(product)}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <ImageIcon className="w-12 h-12 text-gray-300" />
                          )}
                          {!product.is_active && (
                            <div className="absolute top-2 right-2">
                              <Badge variant="secondary">Неактивен</Badge>
                            </div>
                          )}
                        </div>

                        <div className="space-y-2">
                          <h3 className="font-medium text-gray-900 line-clamp-1">{getLocalizedName(product)}</h3>
                          
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-xs">{getCategoryName(product.category)}</Badge>
                            {product.sku && (
                              <span className="text-xs text-gray-400">#{product.sku}</span>
                            )}
                            {product.parent_product_id && (
                              <Badge className="text-[10px] bg-blue-100 text-blue-700 border-blue-200">
                                {product.variant_label || 'вариант'}
                              </Badge>
                            )}
                            {products.some(p => p.parent_product_id === product.id) && (
                              <Badge className="text-[10px] bg-purple-100 text-purple-700 border-purple-200">
                                <Layers className="w-3 h-3 mr-0.5 inline" />
                                группа
                              </Badge>
                            )}
                          </div>
                          
                          <div className="text-xl font-bold text-emerald-700">
                            {formatCurrency(product.retail_price)}
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewProduct(product)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Детали
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`product-menu-${product.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditProduct(product)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Редактировать
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                setSelectedProduct(product);
                                setPriceChangeForm({ new_price: product.retail_price.toString(), reason: '' });
                                setShowPriceChange(true);
                              }}>
                                <DollarSign className="w-4 h-4 mr-2" />
                                Изменить цену
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleDeleteProduct(product.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Удалить
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
            )}
            <div className="mt-4 text-sm text-gray-500">
              Показано {filteredProducts.length} из {products.length} товаров
            </div>
          </TabsContent>

          {/* Sales Report Tab */}
          <TabsContent value="sales">
            {salesReport ? (
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-gray-500">Выручка за месяц</div>
                          <div className="text-2xl font-bold text-emerald-600">{formatCurrency(salesReport.total_revenue)}</div>
                        </div>
                        <TrendingUp className="w-8 h-8 text-emerald-400" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-gray-500">Продано единиц</div>
                          <div className="text-2xl font-bold">{salesReport.total_quantity}</div>
                        </div>
                        <ShoppingCart className="w-8 h-8 text-blue-400" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-gray-500">Товаров продано</div>
                          <div className="text-2xl font-bold">{salesReport.total_products}</div>
                        </div>
                        <Package className="w-8 h-8 text-purple-400" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Products Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Статистика по товарам (за месяц)</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">#</th>
                            <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Товар</th>
                            <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Продано</th>
                            <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Выручка</th>
                            <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Заказов</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {salesReport.products.map((product, idx) => (
                            <tr key={product.product_id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                              <td className="px-4 py-3 font-medium">{product.product_name}</td>
                              <td className="px-4 py-3 text-right">{product.quantity} шт</td>
                              <td className="px-4 py-3 text-right font-semibold text-emerald-600">{formatCurrency(product.revenue)}</td>
                              <td className="px-4 py-3 text-center">{product.orders}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin text-gray-400" />
                  <p className="text-gray-500">Загрузка статистики...</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Stock Alerts Tab */}
          <TabsContent value="stock">
            {stockAlerts ? (
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className={stockAlerts.critical_count > 0 ? 'border-red-200' : ''}>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-gray-500">Критический уровень</div>
                          <div className="text-2xl font-bold text-red-600">{stockAlerts.critical_count}</div>
                        </div>
                        <AlertTriangle className="w-8 h-8 text-red-400" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-gray-500">Требует внимания</div>
                          <div className="text-2xl font-bold text-orange-600">{stockAlerts.total_alerts - stockAlerts.critical_count}</div>
                        </div>
                        <Clock className="w-8 h-8 text-orange-400" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-gray-500">Порог оповещения</div>
                          <div className="text-2xl font-bold">{stockAlerts.threshold} шт</div>
                        </div>
                        <Package className="w-8 h-8 text-gray-400" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Alerts Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Товары с низким остатком</CardTitle>
                    <CardDescription>Менее {stockAlerts.threshold} единиц на складе</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    {stockAlerts.alerts.length === 0 ? (
                      <div className="py-12 text-center text-gray-500">
                        <Package className="w-12 h-12 mx-auto mb-3 text-green-400" />
                        <p className="text-lg font-medium text-green-600">Все запасы в норме!</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b">
                            <tr>
                              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Товар</th>
                              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Категория</th>
                              <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Остаток</th>
                              <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Статус</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {stockAlerts.alerts.map((alert) => (
                              <tr key={alert.product_id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 font-medium">{alert.product_name}</td>
                                <td className="px-4 py-3 text-sm text-gray-500">{alert.category}</td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`text-lg font-bold ${alert.status === 'critical' ? 'text-red-600' : 'text-orange-600'}`}>
                                    {alert.current_stock} шт
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <Badge className={alert.status === 'critical' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'}>
                                    {alert.status === 'critical' ? 'Критический' : 'Низкий'}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin text-gray-400" />
                  <p className="text-gray-500">Загрузка данных об остатках...</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Product Details Dialog */}
      <Dialog open={showProductDetails} onOpenChange={setShowProductDetails}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Карточка товара</DialogTitle>
          </DialogHeader>
          
          {productDetails && (
            <div className="space-y-6">
              {/* Product Info */}
              <div className="flex gap-6">
                <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden shrink-0">
                  {productDetails.product.image_url ? (
                    <img
                      src={productDetails.product.image_url}
                      alt={getLocalizedName(productDetails.product)}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ImageIcon className="w-12 h-12 text-gray-300" />
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold">{getLocalizedName(productDetails.product)}</h2>
                  <p className="text-gray-500 mt-1">{productDetails.product.description}</p>
                  <div className="flex items-center gap-3 mt-3">
                    <span className="text-2xl font-bold text-emerald-700">{formatCurrency(productDetails.product.retail_price)}</span>
                    <Badge variant="outline">{getCategoryName(productDetails.product.category)}</Badge>
                    {productDetails.product.sku && (
                      <span className="text-sm text-gray-400">Артикул: {productDetails.product.sku}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <ShoppingCart className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                    <div className="text-2xl font-bold">{productDetails.sales_stats.total_quantity_sold}</div>
                    <div className="text-xs text-gray-500">Продано</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <TrendingUp className="w-6 h-6 mx-auto mb-2 text-green-500" />
                    <div className="text-xl font-bold">{formatCurrency(productDetails.sales_stats.total_revenue)}</div>
                    <div className="text-xs text-gray-500">Выручка</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <Package className="w-6 h-6 mx-auto mb-2 text-purple-500" />
                    <div className="text-2xl font-bold">{productDetails.stock.total_quantity}</div>
                    <div className="text-xs text-gray-500">На складе</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <DollarSign className="w-6 h-6 mx-auto mb-2 text-orange-500" />
                    <div className="text-xl font-bold">{formatCurrency(productDetails.sales_stats.avg_price_sold)}</div>
                    <div className="text-xs text-gray-500">Средняя цена</div>
                  </CardContent>
                </Card>
              </div>

              {/* Price History */}
              {productDetails.price_history.length > 0 && (
                <div>
                  <Label className="text-base font-semibold mb-3 block flex items-center gap-2">
                    <History className="w-4 h-4" />
                    История цен
                  </Label>
                  <div className="space-y-2">
                    {productDetails.price_history.slice(0, 5).map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <span className="text-gray-500 line-through">{formatCurrency(entry.old_price)}</span>
                          <span className="mx-2">→</span>
                          <span className="font-semibold text-emerald-600">{formatCurrency(entry.new_price)}</span>
                          <span className={`ml-2 text-sm ${entry.change_percent > 0 ? 'text-red-500' : 'text-green-500'}`}>
                            ({entry.change_percent > 0 ? '+' : ''}{entry.change_percent}%)
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500">{formatDate(entry.changed_at)}</div>
                          {entry.reason && (
                            <div className="text-xs text-gray-400">{entry.reason}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Group Pricing */}
              {productDetails.group_pricing.length > 0 && (
                <div>
                  <Label className="text-base font-semibold mb-3 block">Цены для групп</Label>
                  <div className="flex flex-wrap gap-2">
                    {productDetails.group_pricing.map((gp) => (
                      <Badge key={gp.group_id} variant="outline" className="py-1 px-3">
                        {gp.group_name}: <span className="font-semibold text-emerald-600 ml-1">{formatCurrency(gp.special_price)}</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit/Create Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? 'Редактирование товара' : 'Новый товар'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveProduct} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Название (RU) *</Label>
                <Input
                  value={productForm.name}
                  onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                  data-testid="product-name-input"
                />
              </div>
              <div>
                <Label>Название (EN)</Label>
                <Input
                  value={productForm.name_en}
                  onChange={(e) => setProductForm(prev => ({ ...prev, name_en: e.target.value }))}
                  placeholder="English name"
                  data-testid="product-name-en-input"
                />
              </div>
              <div>
                <Label>Название (KZ)</Label>
                <Input
                  value={productForm.name_kz}
                  onChange={(e) => setProductForm(prev => ({ ...prev, name_kz: e.target.value }))}
                  placeholder="Қазақша атауы"
                  data-testid="product-name-kz-input"
                />
              </div>

              <div className="col-span-2">
                <Label>Описание (RU)</Label>
                <Input
                  value={productForm.description}
                  onChange={(e) => setProductForm(prev => ({ ...prev, description: e.target.value }))}
                  data-testid="product-desc-input"
                />
              </div>
              <div>
                <Label>Описание (EN)</Label>
                <Input
                  value={productForm.description_en}
                  onChange={(e) => setProductForm(prev => ({ ...prev, description_en: e.target.value }))}
                  placeholder="English description"
                  data-testid="product-desc-en-input"
                />
              </div>
              <div>
                <Label>Описание (KZ)</Label>
                <Input
                  value={productForm.description_kz}
                  onChange={(e) => setProductForm(prev => ({ ...prev, description_kz: e.target.value }))}
                  placeholder="Қазақша сипаттама"
                  data-testid="product-desc-kz-input"
                />
              </div>

              <div>
                <Label>Базовая цена (для B2B) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={productForm.retail_price}
                  onChange={(e) => setProductForm(prev => ({ ...prev, retail_price: e.target.value }))}
                  required
                  data-testid="product-base-price-input"
                />
                <p className="text-xs text-gray-500 mt-1">Оптовая цена для B2B клиентов</p>
              </div>

              <div>
                <Label>Цена сайта (для B2C/гостей)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={productForm.site_price}
                  onChange={(e) => setProductForm(prev => ({ ...prev, site_price: e.target.value }))}
                  placeholder={productForm.retail_price || "Как базовая"}
                  data-testid="product-site-price-input"
                />
                <p className="text-xs text-gray-500 mt-1">Розничная цена для гостей и B2C. Если пусто — используется базовая</p>
              </div>
              
              <div>
                <Label>Категория</Label>
                <Select
                  value={productForm.category}
                  onValueChange={(v) => setProductForm(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите категорию" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (
                      <SelectItem key={c.id} value={c.id}>{getLocalizedName(c, 'name')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Артикул (SKU)</Label>
                <Input
                  value={productForm.sku}
                  onChange={(e) => setProductForm(prev => ({ ...prev, sku: e.target.value }))}
                  data-testid="product-sku-input"
                />
              </div>

              {/* Поля для интеграции с 1С */}
              <div className="col-span-2 border-t pt-4 mt-2">
                <Label className="text-base font-semibold text-blue-700 flex items-center gap-2 mb-3">
                  <span className="w-2 h-2 rounded-full bg-blue-600"></span>
                  Интеграция с 1С
                </Label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Артикул (для 1С)</Label>
                    <Input
                      value={productForm.article}
                      onChange={(e) => setProductForm(prev => ({ ...prev, article: e.target.value }))}
                      placeholder="Например: 1610"
                      data-testid="product-article-input"
                    />
                  </div>
                  <div>
                    <Label>Код 1С</Label>
                    <Input
                      value={productForm.code_1c}
                      onChange={(e) => setProductForm(prev => ({ ...prev, code_1c: e.target.value }))}
                      placeholder="Например: 00000001610"
                      data-testid="product-code-1c-input"
                    />
                  </div>
                  <div>
                    <Label>NTIN (НКТ)</Label>
                    <Input
                      value={productForm.ntin}
                      onChange={(e) => setProductForm(prev => ({ ...prev, ntin: e.target.value }))}
                      placeholder="Например: 0200261093937"
                      data-testid="product-ntin-input"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <Label>Единица измерения</Label>
                <Select
                  value={productForm.unit_of_measure}
                  onValueChange={(v) => setProductForm(prev => ({ ...prev, unit_of_measure: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите единицу" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {getLocalizedName(u, 'name')} ({getLocalizedName(u, 'short_name')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label>Изображение товара</Label>
                <div className="mt-2 space-y-3">
                  {/* Image Preview */}
                  {productForm.image_url && (
                    <div className="relative w-32 h-32 border rounded-lg overflow-hidden group">
                      <img 
                        src={productForm.image_url} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden w-full h-full items-center justify-center bg-gray-100 text-gray-400">
                        <ImageIcon className="w-8 h-8" />
                      </div>
                      <button
                        type="button"
                        onClick={() => setProductForm(prev => ({ ...prev, image_url: '' }))}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  
                  {/* Drag & Drop Zone */}
                  <div
                    className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all ${
                      uploadingImage 
                        ? 'bg-emerald-50 border-emerald-300' 
                        : 'border-gray-300 hover:border-emerald-500 hover:bg-emerald-50'
                    }`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add('border-emerald-500', 'bg-emerald-50');
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-emerald-500', 'bg-emerald-50');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-emerald-500', 'bg-emerald-50');
                      const file = e.dataTransfer.files?.[0];
                      if (file && file.type.startsWith('image/')) {
                        handleImageUpload({ target: { files: [file] } });
                      } else {
                        toast.error('Пожалуйста, перетащите изображение');
                      }
                    }}
                  >
                    {uploadingImage ? (
                      <div className="flex flex-col items-center gap-2">
                        <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin" />
                        <span className="text-emerald-700 font-medium">Загрузка в Cloudflare...</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-3">
                        <div className="flex items-center gap-2 text-gray-400">
                          <Upload className="w-6 h-6" />
                          <span className="text-lg">Перетащите фото сюда</span>
                        </div>
                        <span className="text-sm text-gray-400">или</span>
                        <div className="flex gap-2">
                          {/* Browse files */}
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageUpload}
                              className="hidden"
                              disabled={uploadingImage}
                            />
                            <span className="inline-flex items-center gap-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
                              <Search className="w-4 h-4" />
                              Выбрать файл
                            </span>
                          </label>
                          
                          {/* Camera capture (works on mobile) */}
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              onChange={handleImageUpload}
                              className="hidden"
                              disabled={uploadingImage}
                            />
                            <span className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                              <Eye className="w-4 h-4" />
                              Камера
                            </span>
                          </label>
                          
                          {/* Choose from gallery */}
                          <button
                            type="button"
                            onClick={openGalleryPicker}
                            className="inline-flex items-center gap-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                          >
                            <ImageIcon className="w-4 h-4" />
                            Галерея
                          </button>
                        </div>
                        <span className="text-xs text-gray-400">JPG, PNG, WebP до 5MB</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Or enter URL manually */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 whitespace-nowrap">или URL:</span>
                    <Input
                      value={productForm.image_url}
                      onChange={(e) => setProductForm(prev => ({ ...prev, image_url: e.target.value }))}
                      placeholder="https://..."
                      className="flex-1 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Hover Image Section */}
              <div className="col-span-2">
                <Label>Hover-изображение (показывается при наведении)</Label>
                <p className="text-xs text-gray-500 mb-2">Например: готовый продукт после выпечки</p>
                <div className="mt-2 flex items-start gap-4">
                  {/* Hover Image Preview */}
                  {productForm.hover_image_url ? (
                    <div className="relative w-24 h-24 border rounded-lg overflow-hidden group flex-shrink-0">
                      <img 
                        src={productForm.hover_image_url} 
                        alt="Hover Preview" 
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <button
                        type="button"
                        onClick={() => setProductForm(prev => ({ ...prev, hover_image_url: '' }))}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center text-gray-300 flex-shrink-0">
                      <ImageIcon className="w-8 h-8" />
                    </div>
                  )}
                  
                  <div className="flex-1 space-y-2">
                    <div className="flex gap-2">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleHoverImageUpload}
                          className="hidden"
                          disabled={uploadingImage}
                        />
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm">
                          <Upload className="w-4 h-4" />
                          Загрузить
                        </span>
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">URL:</span>
                      <Input
                        value={productForm.hover_image_url}
                        onChange={(e) => setProductForm(prev => ({ ...prev, hover_image_url: e.target.value }))}
                        placeholder="https://..."
                        className="flex-1 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-2 flex items-center gap-2">
                <Switch
                  checked={productForm.is_active}
                  onCheckedChange={(checked) => setProductForm(prev => ({ ...prev, is_active: checked }))}
                />
                <Label>Активен</Label>
              </div>

              {/* Online Availability Settings */}
              <div className="col-span-2 border-t pt-4 mt-2">
                <Label className="text-base font-semibold mb-3 block">Настройки онлайн-заказа</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={productForm.online_available}
                      onCheckedChange={(checked) => setProductForm(prev => ({ ...prev, online_available: checked }))}
                    />
                    <Label>Доступен для онлайн-заказа</Label>
                  </div>
                  <div>
                    <Label>Дневной лимит (0 = без лимита)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={productForm.daily_limit}
                      onChange={(e) => setProductForm(prev => ({ ...prev, daily_limit: e.target.value }))}
                      placeholder="0"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Максимум штук в день для онлайн-заказов</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Group Pricing (only for editing) */}
            {editingProduct && (
              <div className="border-t pt-4 mt-4">
                <Label className="text-base font-semibold mb-3 block">Цены для групп клиентов</Label>

                {groupPricing.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {groupPricing.map((gp) => (
                      <div key={gp.group_id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div>
                          <span className="font-medium">{gp.group_name}</span>
                          <span className="mx-2">→</span>
                          {gp.price_type === 'fixed' || gp.special_price ? (
                            <span className="font-bold text-green-600">{formatCurrency(gp.special_price)}</span>
                          ) : (
                            <span className="font-bold text-green-600">-{gp.discount_percentage}%</span>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveGroupPrice(gp.group_id)}
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {availableGroups.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Label className="text-xs text-gray-500">Группа</Label>
                        <Select
                          value={newGroupPrice.group_id}
                          onValueChange={(v) => setNewGroupPrice(prev => ({ ...prev, group_id: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Выберите группу" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableGroups.map(g => (
                              <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-40">
                        <Label className="text-xs text-gray-500">Тип</Label>
                        <Select
                          value={newGroupPrice.price_type}
                          onValueChange={(v) => setNewGroupPrice(prev => ({ ...prev, price_type: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fixed">Фикс. цена</SelectItem>
                            <SelectItem value="percentage">Скидка %</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex gap-2 items-end">
                      {newGroupPrice.price_type === 'fixed' ? (
                        <div className="flex-1">
                          <Label className="text-xs text-gray-500">Цена (₸)</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={newGroupPrice.price}
                            onChange={(e) => setNewGroupPrice(prev => ({ ...prev, price: e.target.value }))}
                            placeholder="Фиксированная цена"
                          />
                        </div>
                      ) : (
                        <div className="flex-1">
                          <Label className="text-xs text-gray-500">Скидка (%)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={newGroupPrice.discount_percentage}
                            onChange={(e) => setNewGroupPrice(prev => ({ ...prev, discount_percentage: e.target.value }))}
                            placeholder="Процент скидки"
                          />
                        </div>
                      )}
                      <Button type="button" onClick={handleAddGroupPrice} className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-group-price-btn">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
                
                {availableGroups.length === 0 && groupPricing.length === 0 && (
                  <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                    <p className="text-sm">Нет групп скидок</p>
                    <Link to={`/${currentLang}/admin/discount-groups`} className="text-sm text-emerald-600 hover:underline">
                      Создать группу
                    </Link>
                  </div>
                )}
                {availableGroups.length === 0 && groupPricing.length > 0 && (
                  <div className="text-center py-3 text-gray-500 bg-gray-50 rounded-lg">
                    <p className="text-sm">Все группы скидок назначены</p>
                    <Link to={`/${currentLang}/admin/discount-groups`} className="text-sm text-emerald-600 hover:underline">
                      Создать ещё группу
                    </Link>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowProductDialog(false)}>
                Отмена
              </Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800">
                {editingProduct ? 'Сохранить' : 'Создать'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Price Change Dialog */}
      <Dialog open={showPriceChange} onOpenChange={setShowPriceChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Изменение цены</DialogTitle>
          </DialogHeader>
          
          {selectedProduct && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Товар: <strong>{selectedProduct.name}</strong>
              </p>
              <p className="text-sm text-gray-600">
                Текущая цена: <strong>{formatCurrency(selectedProduct.retail_price)}</strong>
              </p>

              <div>
                <Label>Новая цена *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={priceChangeForm.new_price}
                  onChange={(e) => setPriceChangeForm(prev => ({ ...prev, new_price: e.target.value }))}
                  required
                />
              </div>

              <div>
                <Label>Причина изменения</Label>
                <Input
                  value={priceChangeForm.reason}
                  onChange={(e) => setPriceChangeForm(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Опционально..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowPriceChange(false)}>
                  Отмена
                </Button>
                <Button onClick={handlePriceChange} className="bg-emerald-700 hover:bg-emerald-800">
                  Изменить цену
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Products Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-emerald-600" />
              Импорт товаров из Excel
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">Инструкция:</h4>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                <li>Скачайте шаблон или экспортируйте текущие товары</li>
                <li>Заполните данные в Excel файле</li>
                <li>Загрузите файл через кнопку ниже</li>
              </ol>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleDownloadTemplate} className="flex-1">
                <Download className="w-4 h-4 mr-2" />
                Скачать шаблон
              </Button>
              <Button variant="outline" onClick={handleExportProducts} disabled={exporting} className="flex-1">
                <Download className="w-4 h-4 mr-2" />
                Экспорт товаров
              </Button>
            </div>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportProducts}
                className="hidden"
                id="import-file-input"
                disabled={importing}
              />
              <label htmlFor="import-file-input" className="cursor-pointer">
                {importing ? (
                  <div className="flex flex-col items-center gap-2">
                    <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin" />
                    <span className="text-gray-600">Импортируем...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <FileSpreadsheet className="w-12 h-12 text-gray-400" />
                    <span className="text-gray-600">Нажмите для выбора файла</span>
                    <span className="text-xs text-gray-400">Формат: .xlsx или .xls</span>
                  </div>
                )}
              </label>
            </div>
            
            {/* Import Results */}
            {importResult && (
              <div className={`p-4 rounded-lg ${importResult.success !== false ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                {importResult.success !== false ? (
                  <>
                    <h4 className="font-medium text-green-800 mb-2">Результат импорта:</h4>
                    <div className="text-sm text-green-700 space-y-1">
                      <p>Создано товаров: <strong>{importResult.created}</strong></p>
                      <p>Обновлено товаров: <strong>{importResult.updated}</strong></p>
                      {importResult.categories_created > 0 && (
                        <p>Создано категорий: <strong>{importResult.categories_created}</strong></p>
                      )}
                      {importResult.units_created > 0 && (
                        <p>Создано единиц измерения: <strong>{importResult.units_created}</strong></p>
                      )}
                      {importResult.total_errors > 0 && (
                        <p className="text-orange-600">Ошибок: <strong>{importResult.total_errors}</strong></p>
                      )}
                    </div>
                    
                    {importResult.errors?.length > 0 && (
                      <div className="mt-3 p-2 bg-orange-50 rounded border border-orange-200">
                        <p className="text-xs font-medium text-orange-700 mb-1">Ошибки:</p>
                        <ul className="text-xs text-orange-600 space-y-0.5">
                          {importResult.errors.slice(0, 5).map((err, idx) => (
                            <li key={idx}>• {err}</li>
                          ))}
                          {importResult.errors.length > 5 && (
                            <li>... и еще {importResult.errors.length - 5} ошибок</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <h4 className="font-medium text-red-800 mb-2">Ошибка импорта</h4>
                    <p className="text-sm text-red-700">{importResult.errors?.[0] || 'Неизвестная ошибка'}</p>
                  </>
                )}
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => { setShowImportDialog(false); setImportResult(null); }}>
              Закрыть
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gallery Picker Dialog */}
      <Dialog open={showGalleryPicker} onOpenChange={setShowGalleryPicker}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-purple-600" />
              Выбрать из галереи
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[60vh]">
            {galleryLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : galleryImages.length === 0 ? (
              <div className="text-center py-12">
                <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Галерея пуста</p>
                <p className="text-sm text-gray-400 mt-1">Загрузите фото или отправьте ссылку контент-мейкерам</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
                {galleryImages.map((img) => (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => selectGalleryImage(img.url)}
                    className="group relative aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-purple-500 transition-all"
                  >
                    <img
                      src={img.url}
                      alt={img.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-xs font-medium px-2 py-1 bg-purple-600 rounded">
                        Выбрать
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowGalleryPicker(false)}>
              Отмена
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Подтверждение удаления
            </DialogTitle>
            <DialogDescription className="text-gray-600 pt-2">
              Вы уверены, что хотите удалить <span className="font-bold text-red-600">{selectedProducts.size}</span> {selectedProducts.size === 1 ? 'товар' : selectedProducts.size < 5 ? 'товара' : 'товаров'}?
              <br /><br />
              <span className="text-red-500 font-medium">
                Это действие необратимо! Все связанные данные о ценах для групп также будут удалены.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowBulkDeleteDialog(false)}
              disabled={bulkDeleting}
              data-testid="cancel-bulk-delete-btn"
            >
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              data-testid="confirm-bulk-delete-btn"
            >
              {bulkDeleting ? 'Удаление...' : 'Да, удалить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Variant Grouping Dialog */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-600" />
              Группировка вариантов
            </DialogTitle>
            <DialogDescription>
              Объедините товары в одну карточку с переключателем вариантов (размер, вкус и т.д.)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <div className="text-sm font-medium text-gray-700 mb-1">Выберите главный товар (родитель):</div>
            {Array.from(selectedProducts).map(id => {
              const p = products.find(pr => pr.id === id);
              if (!p) return null;
              return (
                <div key={id} className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${
                  groupParentId === id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                }`} onClick={() => setGroupParentId(id)}>
                  <input type="radio" checked={groupParentId === id} onChange={() => setGroupParentId(id)} className="accent-blue-600" />
                  <div className="w-10 h-10 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                    {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover" /> : <Package className="w-6 h-6 m-2 text-gray-300" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.name_ru || p.name}</div>
                    <div className="text-xs text-gray-500">{p.retail_price} ₸ | {p.sku || 'без SKU'}</div>
                  </div>
                  <input
                    type="text"
                    placeholder="Вариант (90г, с курицей...)"
                    value={groupVariantLabels[id] || ''}
                    onChange={e => setGroupVariantLabels(prev => ({...prev, [id]: e.target.value}))}
                    onClick={e => e.stopPropagation()}
                    className="w-36 px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    data-testid={`variant-label-${id}`}
                  />
                </div>
              );
            })}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowGroupDialog(false)}>Отмена</Button>
            <Button onClick={handleGroupVariants} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-group-btn">
              <Layers className="w-4 h-4 mr-2" />
              Сгруппировать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminProductsPro;
